import { memo, useCallback } from "react";
import { Box, styled } from "@mui/material";
import MxTypography from "@rf-l1/MxTypography";
import Calendar from "@icons-rf/calendar-icon.svg?react";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { IBuildingFromDetail } from "@rf/property/module/adapters/detail/prepareDetailPropertyBuildings";
import { O_CURRENCY } from "@/src-refactor/modules/shared/model/const/currency.conts";
import MoneyFormat from "@/src-refactor/modules/shared/helpers/moneyFormat.helper";
import { PROPERTIES_STATUS } from "@/shared/models/const/properties/properties-status.const";
import MenuOptionTableDetail from "./MenuOptionTableDetail";

interface MobileViewProps {
  buildings: IBuildingFromDetail[];
  expandedBuildings: number[];
  visibleColumns: any;
  onToggle: (buildingId: number) => void;
  onCopyId: (id: number) => void;
}

const MobileView = memo(
  ({
    buildings,
    expandedBuildings,
    visibleColumns,
    onToggle,
    onCopyId
  }: MobileViewProps) => {
    const getStatusColor = (status: string) => {
      const statusObj = Object.values(PROPERTIES_STATUS).find(
        s => s.name === status
      );
      return statusObj?.color || "var(--m-color-blue-03)";
    };

    const handleToggle = useCallback(
      (buildingId: number) => {
        onToggle(buildingId);
      },
      [onToggle]
    );

    return (
      <MobileContainer>
        {buildings.map((building, buildingIndex) => {
          const isExpanded = expandedBuildings.includes(building.id);
          const unitsToShow = isExpanded ? building.unit : building.unit.slice(0, 1);

          return (
            <BuildingCard key={building.id || buildingIndex}>
              {/* Header del Edificio */}
              <BuildingHeaderMobile>
                <BuildingTitle>
                  {building.nameBuilding || `Edificio ${buildingIndex + 1}`}
                </BuildingTitle>
                {building.deliveryDate && (
                  <DeliveryDateBadge>
                    <Calendar
                      style={{
                        width: "14px",
                        height: "14px",
                        fill: "var(--m-color-blue-03)"
                      }}
                    />
                    <MxTypography
                      variant="caption"
                      sx={{
                        color: "var(--m-color-blue-03)",
                        fontWeight: 600,
                        fontSize: "11px"
                      }}
                    >
                      {building.deliveryDate}
                    </MxTypography>
                  </DeliveryDateBadge>
                )}
              </BuildingHeaderMobile>

              {/* Unidades */}
              {building.unit && building.unit.length > 0 ? (
                <>
                  {unitsToShow.map((unit, unitIndex) => (
                    <UnitCardMobile key={unit.id || unitIndex}>
                      {/* Header de la Unidad */}
                      <UnitHeader>
                        <StatusBadge statusColor={getStatusColor(unit.status)}>
                          {unit.status}
                        </StatusBadge>
                        <UnitIdContainer>
                          <MxTypography
                            variant="body2"
                            fontWeight={700}
                            onClick={() => onCopyId(unit.id)}
                            sx={{
                              cursor: "pointer",
                              "&:hover": { color: "primary.main" }
                            }}
                          >
                            ID: {unit.id}
                          </MxTypography>
                          <MenuOptionTableDetail unit={unit as any} />
                        </UnitIdContainer>
                      </UnitHeader>

                      {/* Nombre de la Unidad */}
                      {visibleColumns.name && (
                        <UnitName>
                          <MxTypography variant="body1" fontWeight={700}>
                            {unit.name}
                          </MxTypography>
                        </UnitName>
                      )}

                      {/* Detalles de la Unidad */}
                      <UnitDetails>
                        {visibleColumns.bedrooms && (
                          <DetailRow>
                            <DetailLabel>Habitaciones</DetailLabel>
                            <DetailValue>{unit.bedrooms}</DetailValue>
                          </DetailRow>
                        )}

                        {visibleColumns.bathrooms && (
                          <DetailRow>
                            <DetailLabel>Baños</DetailLabel>
                            <DetailValue>{unit.bathrooms}</DetailValue>
                          </DetailRow>
                        )}

                        {visibleColumns.parking_spots && (
                          <DetailRow>
                            <DetailLabel>Parqueos</DetailLabel>
                            <DetailValue>{unit.parking}</DetailValue>
                          </DetailRow>
                        )}

                        {visibleColumns.floor_level && (
                          <DetailRow>
                            <DetailLabel>Nivel</DetailLabel>
                            <DetailValue>{unit.floorLevel}</DetailValue>
                          </DetailRow>
                        )}

                        {visibleColumns.sqm_land && (
                          <DetailRow>
                            <DetailLabel>m² Terreno</DetailLabel>
                            <DetailValue>
                              {unit.sqmLand ? unit.sqmLand.toFixed(2) : "0.00"}
                            </DetailValue>
                          </DetailRow>
                        )}

                        {visibleColumns.sqm_construction && (
                          <DetailRow>
                            <DetailLabel>m² Construcción</DetailLabel>
                            <DetailValue>
                              {unit.sqmConstruction
                                ? unit.sqmConstruction.toFixed(2)
                                : "0.00"}
                            </DetailValue>
                          </DetailRow>
                        )}

                        {visibleColumns.price && (
                          <PriceRow>
                            <DetailLabel>Precio</DetailLabel>
                            <PriceValue>
                              {O_CURRENCY[unit.currency as keyof typeof O_CURRENCY]
                                ?.label || unit.currency}{" "}
                              {MoneyFormat(unit.price)}
                            </PriceValue>
                          </PriceRow>
                        )}
                      </UnitDetails>
                    </UnitCardMobile>
                  ))}

                  {/* Botón Ver Más/Menos */}
                  {building.unit.length > 1 && (
                    <ToggleButtonMobile onClick={() => handleToggle(building.id)}>
                      {isExpanded ? (
                        <>
                          <MxTypography variant="body2" fontWeight={600}>
                            Ver menos
                          </MxTypography>
                          <ArrowDropUpIcon />
                        </>
                      ) : (
                        <>
                          <MxTypography variant="body2" fontWeight={600}>
                            Ver más ({building.unit.length - 1} unidades)
                          </MxTypography>
                          <ArrowDropDownIcon />
                        </>
                      )}
                    </ToggleButtonMobile>
                  )}
                </>
              ) : (
                <Box sx={{ p: 3, textAlign: "center" }}>
                  <MxTypography variant="body2" color="text.secondary">
                    No hay unidades disponibles
                  </MxTypography>
                </Box>
              )}
            </BuildingCard>
          );
        })}
      </MobileContainer>
    );
  }
);

MobileView.displayName = "MobileView";

export default MobileView;

// Styled Components
const MobileContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  width: "100%",
  padding: "0 8px"
}));

const BuildingCard = styled(Box)(({ theme }) => ({
  backgroundColor: "white",
  borderRadius: "12px",
  overflow: "hidden",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  marginBottom: "8px"
}));

const BuildingHeaderMobile = styled(Box)(({ theme }) => ({
  backgroundColor: "var(--m-color-blue-03)",
  padding: "16px",
  display: "flex",
  flexDirection: "column",
  gap: "8px"
}));

const BuildingTitle = styled(MxTypography)(({ theme }) => ({
  color: "white",
  fontSize: "16px",
  fontWeight: 700,
  lineHeight: "1.4"
}));

const DeliveryDateBadge = styled(Box)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  backgroundColor: "white",
  borderRadius: "16px",
  padding: "4px 12px",
  alignSelf: "flex-start"
}));

const UnitCardMobile = styled(Box)(({ theme }) => ({
  padding: "16px",
  borderBottom: "1px solid #e0e0e0",
  "&:last-of-type": {
    borderBottom: "none"
  }
}));

const UnitHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "12px"
}));

const StatusBadge = styled(Box)<{ statusColor?: string }>(
  ({ theme, statusColor }) => ({
    backgroundColor: statusColor || "var(--m-color-blue-03)",
    color: "white",
    fontSize: "10px",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    padding: "4px 10px",
    borderRadius: "12px",
    display: "inline-block"
  })
);

const UnitIdContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "8px"
}));

const UnitName = styled(Box)(({ theme }) => ({
  marginBottom: "16px",
  paddingBottom: "12px",
  borderBottom: "1px solid #f0f0f0"
}));

const UnitDetails = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "12px"
}));

const DetailRow = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "8px 0"
}));

const DetailLabel = styled(MxTypography)(({ theme }) => ({
  fontSize: "13px",
  fontWeight: 500,
  color: "#666"
}));

const DetailValue = styled(MxTypography)(({ theme }) => ({
  fontSize: "14px",
  fontWeight: 700,
  color: "#333"
}));

const PriceRow = styled(DetailRow)(({ theme }) => ({
  backgroundColor: "#f8f9fa",
  padding: "12px",
  borderRadius: "8px",
  marginTop: "8px"
}));

const PriceValue = styled(MxTypography)(({ theme }) => ({
  fontSize: "16px",
  fontWeight: 700,
  color: "var(--m-color-blue-03)"
}));

const ToggleButtonMobile = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "4px",
  padding: "12px",
  color: "var(--m-color-blue-03)",
  cursor: "pointer",
  borderTop: "1px solid #e0e0e0",
  "&:hover": {
    backgroundColor: "#f8f9fa"
  },
  svg: {
    color: "var(--m-color-blue-03)",
    fill: "var(--m-color-blue-03)"
  }
}));
